import React, { useState, useEffect } from 'react';
import { ReactComponent as ArrowIcon } from 'src/image/common/down.svg';
import { useLocation } from 'react-router-dom';
import { getPropertieslist } from '../../API/apiService';
import { LocalizedLink } from 'src/components/common/LocalizedLink';
import { ReactComponent as InstagramIcon } from 'src/image/common/insta.svg';

interface PropertyData {
  area_id: number;
  area_name: string;
  area_name_en: string;
  property_id: number;
  chillnn_id: string;
  property_name: string;
  property_name_en: string;
}

interface LocationItem {
  name: string;
  url: string;
  property_id?: number;
}

export const FooterItems: React.FC = () => {
  const location = useLocation();
  const [isMagazinePage, setIsMagazinePage] = useState(false);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const path = location.pathname.toLowerCase();
  const monthlyTopRegex = /^\/(?:en|ja|jp)?\/?monthly\/?$/;
  const hotelTopRegex = /^\/(?:en|ja|jp)?\/?hotel\/?$/;

  const isMonthlyTop = monthlyTopRegex.test(path);
  const isHotelTop = hotelTopRegex.test(path);

  // URL の先頭セグメントから言語を判定 (例: /en/xxx → 英語、それ以外は日本語)
  // 動的な部分はそのまま en/ja 切り替えするが、下記静的テキストは常に英語固定とする
  const useEnglish = location.pathname.split('/')[1] === 'en';

  useEffect(() => {
    const currentUrl = location.pathname.toLowerCase();
    setIsMagazinePage(currentUrl.includes('magazine'));

    getPropertieslist()
      .then((response: any) => {
        if (response.data && Array.isArray(response.data.data)) {
          setProperties(response.data.data);
        } else {
          console.error(
            'getPropertieslist: response.data.data is not an array:',
            response.data
          );
        }
      })
      .catch((error: any) => {
        console.error('Failed to fetch properties:', error);
      });
  }, [location]);

  const isHotelPage = location.pathname.toLowerCase().includes('hotel');
  const isMonthlyPage = location.pathname.toLowerCase().includes('monthly');

  // 各物件の表示名は、動的に英語の場合は property_name_en、そうでなければ property_name を使用
  const locationItems: LocationItem[] = properties.map((prop: PropertyData) => {
    let url = '';
    if (isHotelPage) {
      url = `/hotel/${prop.property_id}`;
    } else if (isMonthlyPage) {
      url = `/monthly/${prop.property_id}`;
    } else {
      url = `/hotel/${prop.property_id}`;
    }
    return {
      name: useEnglish ? prop.property_name_en : prop.property_name,
      url,
      property_id: prop.property_id,
    };
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="py-4 bg-cream border-t border-black relative">
      {/* MD以上の画面表示 */}
      <div className="hidden md:flex justify-between items-start">
        <div className="flex space-x-8">
          {isMagazinePage ? (
            <>
              <div className="w-48">
                <ul className="space-y-3">
                  <li>
                    <LocalizedLink to="/hotel" className="text-black underline">
                      Hotel
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/monthly"
                      className="text-black underline"
                    >
                      Monthly
                    </LocalizedLink>
                  </li>
                </ul>
              </div>
              <div className="w-48">
                <ul className="space-y-3">
                  <li>
                    <LocalizedLink
                      to="/policy"
                      className="text-black underline"
                    >
                      Privacy Policy
                    </LocalizedLink>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Locationsリスト（先頭4件を表示） */}
              <div className="w-48">
                <h2 className="text-lg mb-3 underline">Locations</h2>
                <ul className="space-y-3 ml-3 text-base">
                  {/* TODO:3, 4番目の物件を除外して、先頭4件を表示。filterとれば戻る */}
                  {locationItems.slice(0, 4).map((loc, index) => (
                    <li key={index}>
                      <LocalizedLink
                        to={loc.url}
                        className="text-black underline"
                      >
                        {loc.name}
                      </LocalizedLink>
                    </li>
                  ))}
                </ul>
              </div>
              {/* その他のリンク */}
              <div className="w-48">
                <ul className="space-y-3">
                  <li>
                    <LocalizedLink to="/hotel" className="text-black underline">
                      Hotel
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/monthly"
                      className="text-black underline"
                    >
                      Monthly
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/magazine"
                      className="text-black underline"
                    >
                      Magazine
                    </LocalizedLink>
                  </li>
                  <li>
                    <a
                      href="https://judicious-sodium-d58.notion.site/theapartmenthotel?pvs=4"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black underline"
                    >
                      FAQ
                    </a>
                  </li>
                </ul>
              </div>
            </>
          )}
          {/* 共通コンテンツ */}
          <div className="w-48">
            <ul className="space-y-3">
              <LocalizedLink to="/inquiry" className="text-black underline">
                Contact Form
              </LocalizedLink>
            </ul>
          </div>
          <div className="w-48">
            {/* <p className="text-sm">Contact and Chat</p> */}
            {/* <div className="mt-2 flex space-x-4">
              <a
                href="https://line.me"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LineIcon className="w-5 h-5" />
              </a>
              <a
                href="https://www.whatsapp.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsAppIcon className="w-5 h-5" />
              </a>
            </div> */}
            <div className="flex mt-2">
              <a
                href="https://www.instagram.com/apt_theapartment/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center"
              >
                <span className="text-xs font-medium">follow</span>
                <InstagramIcon className="w-6 h-6 mt-1" />
              </a>
            </div>
          </div>
        </div>
        {/* 右側のTOPボタン */}
        <div className="flex items-start">
          <button onClick={scrollToTop} className="flex items-center text-base">
            <span className="transform rotate-[-180deg]">
              <ArrowIcon className="w-5 h-5 mt-1" />
            </span>
            <span className="pl-1">TOP</span>
          </button>
        </div>
      </div>

      {/* SP以下で表示されるコンテンツ */}
      <div className="md:hidden">
        <div className="grid grid-cols-2 gap-4 mb-8">
          {isMagazinePage ? (
            <>
              <div>
                <ul className="space-y-3">
                  <li>
                    <LocalizedLink to="/hotel" className="text-black underline">
                      Hotel
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/monthly"
                      className="text-black underline"
                    >
                      Monthly
                    </LocalizedLink>
                  </li>
                </ul>
              </div>
              <div>
                <ul className="space-y-3">
                  <li>
                    <LocalizedLink
                      to="/inquiry"
                      className="text-black underline"
                    >
                      Contact Form
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/policy"
                      className="text-black underline"
                    >
                      Privacy Policy
                    </LocalizedLink>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div>
                <h2 className="text-lg mb-3 underline">Locations</h2>
                <ul className="space-y-3 ml-3 text-sm">
                  {/* // TODO:3, 4番目の物件を除外して、先頭4件を表示。filterとれば戻る */}
                  {locationItems.slice(0, 4).map((loc, index) => (
                    <li key={index}>
                      <LocalizedLink
                        to={loc.url}
                        className="text-black underline"
                      >
                        {loc.name}
                      </LocalizedLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <ul className="space-y-3">
                  <li>
                    <LocalizedLink to="/hotel" className="text-black underline">
                      Hotel
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/monthly"
                      className="text-black underline"
                    >
                      Monthly
                    </LocalizedLink>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/magazine"
                      className="text-black underline"
                    >
                      Magazine
                    </LocalizedLink>
                  </li>
                  <li>
                    <a
                      href="https://judicious-sodium-d58.notion.site/theapartmenthotel?pvs=4"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black underline"
                    >
                      FAQ
                    </a>
                  </li>
                  <li>
                    <LocalizedLink
                      to="/inquiry"
                      className="text-black underline"
                    >
                      Contact Form
                    </LocalizedLink>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
        <div className="flex cursor-auto md:hidden">
          <a
            href="https://www.instagram.com/apt_theapartment/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center"
          >
            <span className="text-xs font-medium">follow</span>
            <InstagramIcon className="w-6 h-6 mt-1" />
          </a>
        </div>
        {/* SP用：Privacy Policy と Accommodation Contract のリンクを、著作権表記の上に配置 */}
        {(isMonthlyTop || isHotelTop) && (
          <div className="text-xs text-left pr-4 mb-4 mt-8">
            <LocalizedLink
              to={useEnglish ? '/en/policy' : '/jp/policy'}
              className="text-black underline block"
            >
              Privacy Policy
            </LocalizedLink>
            {isHotelTop && (
              <a
                href="https://drive.google.com/file/d/1OTIH22CRUAAkVdxcojxVPeXPdB0A2eHh/view"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline block mt-2"
              >
                Accommodation Contract
              </a>
            )}
          </div>
        )}
      </div>

      {/* PC用：右下に表示（md以上） */}
      {(isMonthlyTop || isHotelTop) && (
        <div className="hidden md:block md:absolute md:right-0 md:bottom-8 md:pr-4 md:pb-4 text-xs text-right">
          <LocalizedLink
            to={useEnglish ? '/en/policy' : '/jp/policy'}
            className="text-black underline"
          >
            Privacy Policy
          </LocalizedLink>
          {isHotelTop && (
            <div className="mt-3">
              <a
                href="https://drive.google.com/file/d/1OTIH22CRUAAkVdxcojxVPeXPdB0A2eHh/view"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline"
              >
                Accommodation Contract
              </a>
            </div>
          )}
        </div>
      )}

      {/* 既存の著作権表記 */}
      <div className="mt-4 text-xs text-left md:text-right md:absolute md:right-0 md:bottom-0 md:pr-4 md:pb-4">
        <p>c. The Apartment Hotel</p>
      </div>
    </div>
  );
};
export default FooterItems;
